import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletProvider';
import { useToast, readableError } from '../components/Toast';
import Loader from '../components/Loader';
import {
  fetchStats, fetchLevels, fund, distributeBatch, setBlacklist,
  editAccount, editWallet, setLevelConfig, emergencyWithdrawAll, emergencyWithdraw,
  resolveAccounts, parseIncomeFromReceipt, resolveAccount, adminRegister, idOfWallet, fetchUser,
  fetchWithdrawFee, setWithdrawFee,
} from '../services/zen';
import { parseTradingCsv, chunkForDistribution, BATCH_SIZE } from '../utils/csv';
import { money, toUnits, fromUnits, defaultWeekId } from '../utils/format';
import { getNames, nextRound, saveWeekResults, saveProfile } from '../services/api';

export default function Admin({ roles }) {
  const { provider, signer, address, isCorrectNetwork, ensureNetwork } = useWallet();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState(null);

  // distribution state
  const [weekId, setWeekId] = useState(defaultWeekId());
  const [parsed, setParsed] = useState(null); // { rows, skipped, headerErr }
  const [fileName, setFileName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(null); // { matched:[], unmatched:[] }
  const [previewTab, setPreviewTab] = useState('matched');
  const [batches, setBatches] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0, active: false });

  // simple admin inputs
  const [fundAmt, setFundAmt] = useState('');
  const [blUser, setBlUser] = useState('');
  const [editId, setEditId] = useState('');
  const [editAcc, setEditAcc] = useState('');
  const [editWal, setEditWal] = useState('');
  const [ewAmt, setEwAmt] = useState('');

  // admin add-user form
  const [au, setAu] = useState({ wallet: '', account: '', sponsorMt5: '', name: '', email: '', mobile: '' });
  const [auSponsor, setAuSponsor] = useState({ status: 'idle', id: '0', name: '' });

  // withdraw fee (whole %)
  const [feePct, setFeePct] = useState('');

  const isOwner = roles && address && address.toLowerCase() === roles.owner;

  const load = async () => {
    if (!provider || !isCorrectNetwork || !roles) { setLoading(false); return; }
    try {
      setLoading(true);
      const [s, c] = await Promise.all([fetchStats(provider), fetchLevels(provider, roles.defaultReferrerId)]);
      setStats(s);
      setCfg({ percents: c.percents, directReqs: c.directReqs, memberReqs: c.memberReqs });
      try { const f = await fetchWithdrawFee(provider); setFeePct(String(f / 100)); } catch (_) {}
    } catch (_) {} finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => { if (alive) await load(); })();
    return () => { alive = false; };
  }, [provider, isCorrectNetwork]);

  // live sponsor lookup for the add-user form (MT5 -> id -> name)
  useEffect(() => {
    const mt5 = (au.sponsorMt5 || '').replace(/[^0-9]/g, '');
    if (!mt5) { setAuSponsor({ status: 'idle', id: '0', name: '' }); return; }
    if (!provider || !isCorrectNetwork) return;
    let alive = true;
    setAuSponsor((s) => ({ ...s, status: 'checking' }));
    const t = setTimeout(async () => {
      try {
        const id = await resolveAccount(provider, mt5);
        if (!alive) return;
        if (id === '0') { setAuSponsor({ status: 'bad', id: '0', name: '' }); return; }
        let name = '';
        try { const nm = await getNames([id]); name = nm[id]?.name || ''; } catch (_) {}
        if (alive) setAuSponsor({ status: 'ok', id, name: name || '(name unavailable)' });
      } catch (_) { if (alive) setAuSponsor({ status: 'bad', id: '0', name: '' }); }
    }, 450);
    return () => { alive = false; clearTimeout(t); };
  }, [au.sponsorMt5, provider, isCorrectNetwork]);

  const guard = async () => {
    if (!(await ensureNetwork())) { toast.error('Switch to the correct network'); return false; }
    return true;
  };

  // ---------- CSV ----------
  const resetDistribution = () => {
    setParsed(null); setBatches([]); setVerified(null); setFileName('');
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setVerified(null);
    setBatches([]);
    const reader = new FileReader();
    reader.onload = () => {
      const res = parseTradingCsv(String(reader.result));
      if (res.headerErr) { toast.error(res.headerErr); setParsed(null); return; }
      setParsed(res);
      toast.info(`${res.rows.length} payable rows · ${res.skipped} skipped. Verify before distributing.`);
    };
    reader.readAsText(file);
  };

  // cross-check every CSV account against the contract
  const verify = async () => {
    if (!parsed || !parsed.rows.length) { toast.error('Upload a CSV first'); return; }
    if (!(await guard())) return;
    try {
      setVerifying(true);
      const accounts = parsed.rows.map((r) => r.account);
      const map = await resolveAccounts(provider, accounts);
      const matched = [];
      const unmatched = [];
      parsed.rows.forEach((r) => {
        const id = map[r.account] || '0';
        if (id !== '0') matched.push({ ...r, userId: id });
        else unmatched.push(r);
      });

      // decorate matched rows with names from the DB (keyed by user id)
      if (matched.length) {
        try {
          const nameMap = await getNames(matched.map((m) => m.userId));
          matched.forEach((m) => { m.name = nameMap[m.userId]?.name || '—'; });
        } catch (_) {
          matched.forEach((m) => { m.name = '—'; });
        }
      }

      setVerified({ matched, unmatched });
      setBatches(chunkForDistribution(matched)); // only pay matched accounts
      setPreviewTab(unmatched.length ? 'unmatched' : 'matched');
      toast.success(`Verified: ${matched.length} matched · ${unmatched.length} not registered`);
    } catch (e) {
      toast.error('Verify failed: ' + readableError(e));
    } finally {
      setVerifying(false);
    }
  };

  const runDistribution = async () => {
    if (!verified) { toast.error('Verify the CSV first'); return; }
    if (!batches.length) { toast.error('No matched accounts to distribute'); return; }
    if (!(await guard())) return;
    setProgress({ done: 0, total: batches.length, active: true });
    try {
      // round number for this distribution (this becomes "this week")
      let round;
      try { round = await nextRound(); } catch (_) { round = 0; }

      // accumulate income per recipient across all chunks (from receipt events)
      const incomeByUser = {}; // userId -> BigNumber
      const log = [];          // individual income line-items for reports

      for (let i = 0; i < batches.length; i++) {
        const b = batches[i];
        const receipt = await distributeBatch(signer, weekId, b.accounts, b.profits); // dedup-safe by weekId
        const items = parseIncomeFromReceipt(signer, receipt);
        items.forEach((it) => {
          const prev = incomeByUser[it.userId] || ethers.BigNumber.from(0);
          incomeByUser[it.userId] = prev.add(it.amount);
          log.push({ userId: it.userId, fromUser: it.fromUser, level: it.level, amount: it.amount.toString() });
        });
        setProgress({ done: i + 1, total: batches.length, active: true });
      }

      // profit per earner straight from the verified CSV (matched rows have userId)
      const profits = verified.matched.map((r) => ({ userId: r.userId, amount: r.profitUnits.toString() }));
      const incomes = Object.keys(incomeByUser).map((uid) => ({ userId: uid, amount: incomeByUser[uid].toString() }));

      // save this distribution's results (this week profit + income + log)
      if (round > 0) {
        try { await saveWeekResults({ round, profits, incomes, log }); }
        catch (_) { toast.info('Distributed on-chain, but saving this-week display data failed — re-run is safe.'); }
      }

      toast.success('Weekly distribution complete');
      resetDistribution();
      await load();
    } catch (e) {
      toast.error('Stopped: ' + readableError(e) + ' (re-running is safe — paid rows skip)');
    } finally {
      setProgress((p) => ({ ...p, active: false }));
    }
  };

  // ---------- simple actions ----------
  const doFund = async () => {
    if (!fundAmt || Number(fundAmt) <= 0) { toast.error('Enter an amount'); return; }
    if (!(await guard())) return;
    try { setBusy(true); await fund(signer, toUnits(fundAmt)); toast.success('Contract funded'); setFundAmt(''); await load(); }
    catch (e) { toast.error(readableError(e)); } finally { setBusy(false); }
  };

  const doBlacklist = async (status) => {
    if (!blUser) { toast.error('Enter a user ID'); return; }
    if (!(await guard())) return;
    try { setBusy(true); await setBlacklist(signer, blUser.replace(/[^0-9]/g, ''), status); toast.success(status ? 'User blacklisted' : 'User restored'); }
    catch (e) { toast.error(readableError(e)); } finally { setBusy(false); }
  };

  const doEditAccount = async () => {
    if (!editId || !editAcc) { toast.error('User ID and new account required'); return; }
    if (!(await guard())) return;
    try { setBusy(true); await editAccount(signer, editId.replace(/[^0-9]/g, ''), editAcc.replace(/[^0-9]/g, '')); toast.success('Account number updated'); }
    catch (e) { toast.error(readableError(e)); } finally { setBusy(false); }
  };

  const doEditWallet = async () => {
    if (!editId || !editWal) { toast.error('User ID and new wallet required'); return; }
    if (!(await guard())) return;
    try { setBusy(true); await editWallet(signer, editId.replace(/[^0-9]/g, ''), editWal.trim()); toast.success('Wallet updated'); }
    catch (e) { toast.error(readableError(e)); } finally { setBusy(false); }
  };

  const saveLevel = async (i) => {
    if (!(await guard())) return;
    try {
      setBusy(true);
      await setLevelConfig(signer, i, cfg.percents[i], cfg.directReqs[i], cfg.memberReqs[i]);
      toast.success(`Level ${i + 1} updated`);
    } catch (e) { toast.error(readableError(e)); } finally { setBusy(false); }
  };

  const saveFee = async () => {
    const pct = Number((feePct || '').replace(/[^0-9.]/g, ''));
    if (isNaN(pct) || pct < 0 || pct > 20) { toast.error('Fee must be between 0 and 20%'); return; }
    if (!(await guard())) return;
    try {
      setBusy(true);
      await setWithdrawFee(signer, Math.round(pct * 100)); // % -> bps
      toast.success(`Withdraw fee set to ${pct}%`);
    } catch (e) { toast.error(readableError(e)); } finally { setBusy(false); }
  };

  const setCfgVal = (key, i) => (e) => {
    const val = Number(e.target.value.replace(/[^0-9]/g, '') || 0);
    setCfg((c) => { const next = { ...c, [key]: [...c[key]] }; next[key][i] = val; return next; });
  };

  // percent input: user types whole/decimal percent (e.g. 10 or 2.5),
  // we store it as basis points internally (10% -> 1000 bps)
  const setPercent = (i) => (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    const pct = raw === '' ? 0 : Number(raw);
    const bps = Math.round(pct * 100);
    setCfg((c) => { const next = { ...c, percents: [...c.percents] }; next.percents[i] = bps; return next; });
  };
  const pctOf = (bps) => {
    const v = bps / 100;
    return Number.isInteger(v) ? String(v) : String(v);
  };

  const doEmergency = async () => {
    if (!window.confirm('Withdraw ALL USDT from the contract to the owner?')) return;
    if (!(await guard())) return;
    try { setBusy(true); await emergencyWithdrawAll(signer); toast.success('Emergency withdrawal sent'); await load(); }
    catch (e) { toast.error(readableError(e)); } finally { setBusy(false); }
  };

  const doEmergencyAmount = async () => {
    if (!ewAmt || Number(ewAmt) <= 0) { toast.error('Enter an amount'); return; }
    if (!window.confirm(`Withdraw ${ewAmt} USDT to the owner?`)) return;
    if (!(await guard())) return;
    try { setBusy(true); await emergencyWithdraw(signer, toUnits(ewAmt)); toast.success('Withdrawal sent'); setEwAmt(''); await load(); }
    catch (e) { toast.error(readableError(e)); } finally { setBusy(false); }
  };

  const setAuVal = (k) => (e) => setAu((s) => ({ ...s, [k]: e.target.value }));

  const doAddUser = async () => {
    const wallet = au.wallet.trim();
    const account = au.account.replace(/[^0-9]/g, '');
    const sponsorMt5 = au.sponsorMt5.replace(/[^0-9]/g, '');
    if (!/^0x[a-fA-F0-9]{40}$/.test(wallet)) { toast.error('Enter a valid wallet address'); return; }
    if (!account) { toast.error('Enter the MT5 account number'); return; }
    if (!au.name.trim()) { toast.error('Enter the name'); return; }
    if (au.email && !/^\S+@\S+\.\S+$/.test(au.email)) { toast.error('Enter a valid email'); return; }
    if (!(await guard())) return;

    // resolve sponsor: blank -> company default; else MT5 -> id
    let referrer;
    if (!sponsorMt5) {
      referrer = roles ? roles.defaultReferrerId : '0';
    } else {
      const id = await resolveAccount(provider, sponsorMt5);
      if (id === '0') { toast.error('Sponsor MT5 not found. Check it or leave blank for company default.'); return; }
      referrer = id;
    }

    try {
      setBusy(true);
      // already registered?
      const existing = await idOfWallet(provider, wallet);
      if (existing !== '0') { toast.error('That wallet is already registered'); setBusy(false); return; }

      await adminRegister(signer, wallet, referrer, account);

      // read back the new user's id, then save PII + send welcome email
      const u = await fetchUser(provider, wallet);
      try {
        await saveProfile({
          wallet, userId: u.id, name: au.name.trim(), mobile: au.mobile.trim(),
          email: au.email.trim(), accountNumber: account, referrerId: referrer,
        });
      } catch (_) { toast.info('Registered on-chain. Profile/email sync failed — can retry.'); }

      toast.success(`User registered (ID ${u.id})`);
      setAu({ wallet: '', account: '', sponsorMt5: '', name: '', email: '', mobile: '' });
      await load();
    } catch (e) {
      toast.error(readableError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader messages={['Loading admin', 'Reading contract state']} />;

  return (
    <div>
      {(busy || progress.active) && (
        <Loader messages={progress.active
          ? [`Distributing batch ${progress.done}/${progress.total}`, 'Confirm each tx in wallet']
          : ['Confirm in your wallet', 'Processing']} />
      )}

      <h1 className="page-title">Admin Console</h1>

      <div className="stat-grid">
        <div className="stat"><div className="k">Total Users</div><div className="v blue">{stats?.totalUsers}</div></div>
        <div className="stat"><div className="k">Pool Balance</div><div className="v gold">{stats ? money(stats.usdtBalance) : '0'}</div></div>
      </div>

      {/* ADD USER */}
      <div className="spacer-18" />
      <div className="card">
        <div className="card-title">Register A User</div>
        <div className="field"><label>User Wallet Address</label>
          <input placeholder="0x…" value={au.wallet} onChange={setAuVal('wallet')} /></div>
        <div className="field"><label>MT5 Account Number</label>
          <input inputMode="numeric" placeholder="e.g. 53504089" value={au.account} onChange={setAuVal('account')} /></div>
        <div className="field"><label>Sponsor MT5 (blank = company default)</label>
          <input inputMode="numeric" placeholder="Sponsor's MT5 number" value={au.sponsorMt5} onChange={setAuVal('sponsorMt5')} />
          {(au.sponsorMt5 || '').replace(/[^0-9]/g, '') !== '' && (
            <div style={{ marginTop: 7, fontSize: 12 }}>
              {auSponsor.status === 'checking' && <span className="muted">Checking…</span>}
              {auSponsor.status === 'ok' && <span style={{ color: 'var(--up)' }}>✓ Sponsor: {auSponsor.name} (ID {auSponsor.id})</span>}
              {auSponsor.status === 'bad' && <span style={{ color: 'var(--down)' }}>✗ Not found — check the number or leave blank</span>}
            </div>
          )}
        </div>
        <div className="field"><label>Full Name</label>
          <input placeholder="User's name" value={au.name} onChange={setAuVal('name')} /></div>
        <div className="field"><label>Email (welcome mail goes here)</label>
          <input type="email" placeholder="user@email.com" value={au.email} onChange={setAuVal('email')} /></div>
        <div className="field"><label>Mobile</label>
          <input inputMode="tel" placeholder="+91…" value={au.mobile} onChange={setAuVal('mobile')} /></div>
        <button className="btn" onClick={doAddUser} disabled={busy || (((au.sponsorMt5 || '').replace(/[^0-9]/g, '') !== '') && auSponsor.status !== 'ok')}>Register User</button>
        <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
          Registers under the entered wallet (you pay gas). Make sure the wallet address is correct — funds are claimed to it.
        </p>
      </div>

      {/* FUND */}
      <div className="spacer-18" />
      <div className="card">
        <div className="card-title">Fund Reward Pool</div>
        <div className="field"><label>USDT Amount</label>
          <input inputMode="decimal" placeholder="e.g. 5000" value={fundAmt} onChange={(e) => setFundAmt(e.target.value)} /></div>
        <button className="btn" onClick={doFund} disabled={busy}>Approve &amp; Fund</button>
      </div>

      {/* WITHDRAW FEE */}
      <div className="spacer-18" />
      <div className="card">
        <div className="card-title">Withdraw Fee</div>
        <div className="field"><label>Fee on each claim (%)</label>
          <input inputMode="decimal" placeholder="e.g. 1" value={feePct} onChange={(e) => setFeePct(e.target.value)} /></div>
        <button className="btn" onClick={saveFee} disabled={busy}>Update Fee</button>
        <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
          Deducted from each user's claim and sent to the fee wallet (admin by default). Max 20%.
        </p>
      </div>

      {/* DISTRIBUTE */}
      <div className="spacer-18" />
      <div className="card">
        <div className="card-title">Weekly Distribution</div>
        <div className="field"><label>Week ID</label>
          <input inputMode="numeric" value={weekId} onChange={(e) => setWeekId(Number(e.target.value.replace(/[^0-9]/g, '') || 0))} /></div>

        <div className="field">
          <label>Upload Trading CSV</label>
          <label className={'file-drop' + (fileName ? ' has-file' : '')}>
            <svg viewBox="0 0 24 24">
              {fileName
                ? <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                : <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />}
            </svg>
            <span className="fd-title">{fileName || 'Tap to choose CSV file'}</span>
            <span className="fd-sub">{fileName ? 'File loaded — verify below' : 'Account Number + Closed Profit columns'}</span>
            <input type="file" accept=".csv,text/csv" onChange={onFile} />
          </label>
        </div>

        {parsed && !verified && (
          <>
            <div className="banner">
              <b className="mono">{parsed.rows.length}</b> payable rows ·
              <b className="mono"> {parsed.skipped}</b> skipped (zero / invalid profit)
            </div>
            <button className="btn" onClick={verify} disabled={verifying}>
              {verifying ? <span className="btn-spin" /> : 'Verify Against Contract'}
            </button>
            <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
              Checks each account against the contract before any funds move.
            </p>
          </>
        )}

        {verified && (
          <>
            <div className="preview-tabs">
              <div className={'preview-tab ok' + (previewTab === 'matched' ? ' active' : '')} onClick={() => setPreviewTab('matched')}>
                Matched<span className="badge">{verified.matched.length}</span>
              </div>
              <div className={'preview-tab warn' + (previewTab === 'unmatched' ? ' active' : '')} onClick={() => setPreviewTab('unmatched')}>
                Not Registered<span className="badge">{verified.unmatched.length}</span>
              </div>
            </div>

            {previewTab === 'matched' ? (
              <div className="preview-scroll">
                <table className="tbl">
                  <thead><tr><th>Account</th><th>Name</th><th>ID</th><th>Profit</th></tr></thead>
                  <tbody>
                    {verified.matched.map((r) => (
                      <tr key={r.account}>
                        <td>{r.account}</td>
                        <td>{r.name || '—'}</td>
                        <td style={{ color: 'var(--blue-bright)' }}>{r.userId}</td>
                        <td style={{ color: 'var(--gold-bright)' }}>{fromUnits(r.profitUnits)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="preview-scroll">
                {verified.unmatched.length === 0 ? (
                  <p className="muted center" style={{ padding: 16, fontSize: 12.5 }}>All accounts are registered ✓</p>
                ) : (
                  <table className="tbl">
                    <thead><tr><th>Account</th><th>Profit</th><th>Status</th></tr></thead>
                    <tbody>
                      {verified.unmatched.map((r) => (
                        <tr key={r.account}>
                          <td>{r.account}</td>
                          <td>{fromUnits(r.profitUnits)}</td>
                          <td><span className="tag locked">Not registered</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {verified.unmatched.length > 0 && (
              <div className="banner warn" style={{ marginTop: 12 }}>
                {verified.unmatched.length} account(s) in the CSV are not registered and will be skipped.
                Register them first if they should be paid this week.
              </div>
            )}

            <div className="spacer-12" />
            <button className="btn gold" onClick={runDistribution} disabled={busy || progress.active || !batches.length}>
              {progress.active ? `Batch ${progress.done}/${progress.total}…` : `Approve & Distribute (${verified.matched.length})`}
            </button>
            <button className="btn ghost" onClick={resetDistribution} disabled={progress.active} style={{ marginTop: 10 }}>
              Cancel / Upload Different File
            </button>
            <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
              Distributes to {verified.matched.length} matched account(s) in {batches.length} batch(es) of {BATCH_SIZE}.
              Re-running the same Week ID never double-pays.
            </p>
          </>
        )}
      </div>

      {/* LEVEL CONFIG */}
      <div className="spacer-18" />
      <div className="card">
        <div className="card-title">Level Configuration</div>
        <p className="muted" style={{ fontSize: 11.5, marginBottom: 14 }}>
          Showing current on-chain values. Enter the percentage directly — type <b>10</b> for 10%.
        </p>
        {cfg && [0, 1, 2, 3, 4].map((i) => (
          <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,.05)', paddingBottom: 12, marginBottom: 12 }}>
            <div className="row-between" style={{ marginBottom: 8 }}>
              <span className="lvl-name">
                <span className="lvl-badge">L{i + 1}</span>
                <span className="lvl-pct">{pctOf(cfg.percents[i])}%</span>
              </span>
              <button className="pill" onClick={() => saveLevel(i)} disabled={busy}>Save</button>
            </div>
            <div className="stat-grid">
              <div className="field" style={{ margin: 0 }}><label>Percentage (%)</label>
                <input inputMode="decimal" value={pctOf(cfg.percents[i])} onChange={setPercent(i)} /></div>
              <div className="field" style={{ margin: 0 }}><label>Direct Req</label>
                <input inputMode="numeric" value={cfg.directReqs[i]} onChange={setCfgVal('directReqs', i)} /></div>
              <div className="field" style={{ margin: 0 }}><label>Members Req</label>
                <input inputMode="numeric" value={cfg.memberReqs[i]} onChange={setCfgVal('memberReqs', i)} /></div>
              <div className="field" style={{ margin: 0 }}><label>Level</label>
                <input value={`Level ${i + 1}`} readOnly /></div>
            </div>
          </div>
        ))}
        <p className="muted" style={{ fontSize: 11.5 }}>
          Current total across L1–L5: <b style={{ color: 'var(--gold-bright)' }}>{pctOf(cfg ? cfg.percents.reduce((a, b) => a + b, 0) : 0)}%</b>
        </p>
      </div>

      {/* BLACKLIST */}
      <div className="spacer-18" />
      <div className="card">
        <div className="card-title">Blacklist</div>
        <div className="field"><label>User ID</label>
          <input inputMode="numeric" placeholder="Member ID" value={blUser} onChange={(e) => setBlUser(e.target.value)} /></div>
        <div className="stat-grid">
          <button className="btn ghost" onClick={() => doBlacklist(true)} disabled={busy} style={{ borderColor: 'var(--down)', color: '#ffb3b8' }}>Blacklist</button>
          <button className="btn ghost" onClick={() => doBlacklist(false)} disabled={busy}>Restore</button>
        </div>
      </div>

      {/* EDIT ACCOUNT / WALLET */}
      <div className="spacer-18" />
      <div className="card">
        <div className="card-title">Edit Member</div>
        <div className="field"><label>User ID</label>
          <input inputMode="numeric" placeholder="Member ID" value={editId} onChange={(e) => setEditId(e.target.value)} /></div>
        <div className="field"><label>New MT5 Account</label>
          <input inputMode="numeric" placeholder="New account number" value={editAcc} onChange={(e) => setEditAcc(e.target.value)} /></div>
        <button className="btn ghost" onClick={doEditAccount} disabled={busy}>Update Account Number</button>
        <div className="spacer-12" />
        <div className="field"><label>New Wallet Address</label>
          <input placeholder="0x…" value={editWal} onChange={(e) => setEditWal(e.target.value)} /></div>
        <button className="btn ghost" onClick={doEditWallet} disabled={busy}>Update Wallet</button>
      </div>

      {/* EMERGENCY (owner only) */}
      {isOwner && (
        <>
          <div className="spacer-18" />
          <div className="card" style={{ borderColor: 'rgba(234,57,67,.4)' }}>
            <div className="card-title" style={{ color: '#ffb3b8' }}>Danger Zone</div>
            <div className="field"><label>Withdraw Amount (USDT)</label>
              <input inputMode="decimal" placeholder="e.g. 1000" value={ewAmt} onChange={(e) => setEwAmt(e.target.value)} /></div>
            <button className="btn ghost" onClick={doEmergencyAmount} disabled={busy} style={{ borderColor: 'var(--down)', color: '#ffb3b8' }}>
              Withdraw Custom Amount
            </button>
            <div className="spacer-12" />
            <button className="btn ghost" onClick={doEmergency} disabled={busy} style={{ borderColor: 'var(--down)', color: '#ffb3b8' }}>
              Emergency Withdraw All USDT
            </button>
          </div>
        </>
      )}
      <div className="spacer-18" />
    </div>
  );
}
